import React from 'react';
import { RecipeIngredient, Product, QuantityUnit } from 'utils/grocyApi.ts';
import { formatQuantityUnit } from 'utils/quantityUnitHelpers.ts';
import { useUnitConversion } from 'hooks/useUnitConversion.ts';
import './RecipeIngredientItem.css';

interface RecipeIngredientItemProps {
  ingredient: RecipeIngredient;
  product: Product;
  recipeUnit?: QuantityUnit;
  stockUnit?: QuantityUnit;
  shortfallAmount: number;
  shoppingListCoverageAmount: number;
  isCoveredByShoppingList: boolean;
  isPartiallyCoveredByShoppingList: boolean;
  checked: boolean;
  onToggle: () => void;
  selectorLabel?: string;
  previewText?: string;
}

const RecipeIngredientItem: React.FC<RecipeIngredientItemProps> = ({
  ingredient,
  product,
  recipeUnit,
  stockUnit,
  shortfallAmount,
  shoppingListCoverageAmount,
  isCoveredByShoppingList,
  isPartiallyCoveredByShoppingList,
  checked,
  onToggle,
  selectorLabel,
  previewText,
}) => {
  const conversionService = useUnitConversion();

  if (!conversionService) {
    return (
      <div className="recipe-ingredient-item">
        <div className="recipe-ingredient-item__content">
          <div className="recipe-ingredient-item__info">
            <div className="recipe-ingredient-item__name">
              {product.name}
            </div>
            <div className="recipe-ingredient-item__amount">
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  const displayData = conversionService.getIngredientDisplayAmount(ingredient, product);
  const stockComparison = conversionService.getIngredientStockComparison(ingredient, product);

  // Get aggregated stock for display
  const aggregatedStock = conversionService.getAggregatedStock(product.id);

  // Round up stock amount if only_check_single_unit_in_stock is 1 and unit is "Pack"
  const displayStock = (ingredient.only_check_single_unit_in_stock === 1 && stockUnit?.name === "Pack")
    ? Math.ceil(aggregatedStock)
    : aggregatedStock;
  const shoppingListCoverageDisplay = stockUnit
    ? formatQuantityUnit(shoppingListCoverageAmount, stockUnit)
    : String(shoppingListCoverageAmount);
  const shortfallDisplay = stockUnit
    ? formatQuantityUnit(shortfallAmount, stockUnit)
    : String(shortfallAmount);
  const stateClassName = stockComparison.isAvailable
    ? 'recipe-ingredient-item--available'
    : isCoveredByShoppingList
      ? 'recipe-ingredient-item--covered'
      : isPartiallyCoveredByShoppingList
        ? 'recipe-ingredient-item--partial'
        : 'recipe-ingredient-item--unavailable';
  const statusIcon = stockComparison.isAvailable
    ? '✅'
    : isCoveredByShoppingList
      ? '🛒'
      : isPartiallyCoveredByShoppingList
        ? '🛍️'
        : '🔴';
  const statusClassName = stockComparison.isAvailable
    ? 'recipe-ingredient-item__status--available'
    : isCoveredByShoppingList
      ? 'recipe-ingredient-item__status--covered'
      : isPartiallyCoveredByShoppingList
        ? 'recipe-ingredient-item__status--partial'
        : 'recipe-ingredient-item__status--unavailable';

  return (
    <label className={`recipe-ingredient-item ${stateClassName}`}>
      <div className="recipe-ingredient-item__content">
        <span
          className="recipe-ingredient-item__selector"
          aria-label={selectorLabel ?? `Select ${product.name} for shopping list`}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggle}
          />
        </span>
        <div className="recipe-ingredient-item__info">
          <div className="recipe-ingredient-item__name">
            {product.name}
          </div>
          <div className="recipe-ingredient-item__amount">
            {formatQuantityUnit(displayData.amount, displayData.unit)}
            {ingredient.variable_amount && (
              <span className="recipe-ingredient-item__variable"> ({ingredient.variable_amount})</span>
            )}
          </div>
        </div>
        <div className="recipe-ingredient-item__stock">
          <span className="recipe-ingredient-item__stock-amount">
            {stockUnit ? formatQuantityUnit(displayStock, stockUnit) : displayStock}
          </span>
          <span
            className={`recipe-ingredient-item__status ${statusClassName}`}
            title={
              stockComparison.isAvailable
                ? 'In stock'
                : isCoveredByShoppingList
                  ? `Shopping list covers ${shoppingListCoverageDisplay}`
                  : isPartiallyCoveredByShoppingList
                    ? `Shopping list covers ${shoppingListCoverageDisplay} of ${shortfallDisplay} shortfall`
                    : 'Not in stock and not covered by the shopping list'
            }
          >
            {statusIcon}
          </span>
        </div>
      </div>
      {!stockComparison.isAvailable && shoppingListCoverageAmount > 0 && (
        <div className="recipe-ingredient-item__coverage">
          {isCoveredByShoppingList
            ? `Shopping list covers ${shoppingListCoverageDisplay}`
            : `Shopping list covers ${shoppingListCoverageDisplay} of ${shortfallDisplay} shortfall`}
        </div>
      )}
      {previewText && (
        <div className="recipe-ingredient-item__consume-preview">
          {previewText}
        </div>
      )}
      {ingredient.note && (
        <div className="recipe-ingredient-item__note">
          {ingredient.note}
        </div>
      )}
    </label>
  );
};

export default RecipeIngredientItem; 
